import { useAsyncState } from "@vueuse/core";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import type { Router } from "vue-router";

type Opts<T> = {
	getUser(): Promise<T>;
	authHostname: string;
};

type TRPCClientError = {
	value: {
		data: {
			httpStatus: number;
			code: string;
		};
	};
};

type AuthState = "authenticating" | "authenticated" | "unauthorized";

export function createUserStore<T>({ getUser, authHostname }: Opts<T>) {
	return defineStore("user", () => {
		// Don't fetch user if already on unauthorized page to prevent redirect loop
		const isOnUnauthorizedPage = window.location.pathname === "/unauthorized";
		const shouldFetchUser = !isOnUnauthorizedPage;

		const { state, error } = useAsyncState(
			shouldFetchUser ? getUser : async () => ({}) as T,
			{} as T,
		);
		const authState = ref<AuthState>(
			isOnUnauthorizedPage ? "unauthorized" : "authenticating",
		);

		watch(error, (value) => {
			console.error(value);
			const redirect = encodeURIComponent(window.location.href);
			const errorStatus = (error as TRPCClientError)?.value?.data.httpStatus;
			// Only redirect if no cookie is set or data from getUser is null
			if (errorStatus === 401) {
				window.location.assign(
					`${authHostname}/login?redirect_uri=${redirect}`,
				);
			}
			// Only redirect if role is not assigned
			else if (errorStatus === 403) {
				authState.value = "unauthorized";
			}
		});

		watch(state, () => {
			if (shouldFetchUser) {
				authState.value = "authenticated";
			}
		});

		return {
			user: state,
			authState,
			logout(queryParams?: string) {
				window.location.assign(`${authHostname}/logout${queryParams ?? ""}`);
			},
		};
	});
}

type RedirectOnAuthenticated = {
	router: Router;
	authStore: ReturnType<ReturnType<typeof createUserStore>>;
	unauthorizedRoute: string;
};

/**
 * Monitor the user's state and redirect to the login page if they are not authenticated.
 * If they attempt to visit another page while unauthenticated then prevent them.
 */
export function redirectOnUnauthenticated({
	router,
	authStore,
	unauthorizedRoute,
}: RedirectOnAuthenticated) {
	authStore.$subscribe(async (_, value) => {
		await router.isReady();
		const currentPage = router.currentRoute.value.path;
		if (
			value.authState === "unauthorized" &&
			currentPage !== unauthorizedRoute
		) {
			router.push(unauthorizedRoute);
		}
	});

	router.beforeEach((to) => {
		if (
			authStore.authState === "unauthorized" &&
			to.path !== unauthorizedRoute
		) {
			return unauthorizedRoute;
		}
	});
}
